import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Play, CheckCircle, Clock, AlertCircle, 
  FileText, Layout, Lock, Sparkles 
} from 'lucide-react';
import Footer from './Footer';
import './ProfileCard.css';

const ProfileCard = ({ darkMode }) => {
  const navigate = useNavigate();
  const [lessons, setLessons] = useState([]);
  const [quizUrl, setQuizUrl] = useState("");
  const [loading, setLoading] = useState(true);
  const [lessonProgress, setLessonProgress] = useState({});
  const [lessonAccess, setLessonAccess] = useState({});
  const [hoveredLockedId, setHoveredLockedId] = useState(null); // لحفظ حالة الرسالة التحفيزية

  // جلب التقدم لكل درس
  const fetchLessonProgress = async (lessonId) => {
    try {
      const authToken = localStorage.getItem('authToken');
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/video-progress/${lessonId}`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      setLessonProgress(prev => ({ ...prev, [lessonId]: response.data }));
    } catch (err) {
      setLessonProgress(prev => ({ ...prev, [lessonId]: { percentage: 0, status: 'not_started' } }));
    }
  };

  // التحقق من صلاحية الدخول للدرس
  const fetchLessonAccess = async (lessonId) => {
    try {
      const authToken = localStorage.getItem('authToken');
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/lessons/${lessonId}/can-access`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      setLessonAccess(prev => ({ ...prev, [lessonId]: response.data.status }));
    } catch (err) {
      setLessonAccess(prev => ({ ...prev, [lessonId]: false }));
    }
  };

  const fetchLessons = useCallback(async () => {
    setLoading(true);
    try {
      const packageId = localStorage.getItem('package_id');
      const authToken = localStorage.getItem('authToken');
      if (!authToken || !packageId) { navigate('/Signin'); return; }

      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/lessons/package/${packageId}`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });

      setLessons(response.data);
      
      response.data.forEach(lesson => {
        fetchLessonProgress(lesson.id);
        fetchLessonAccess(lesson.id);
      });

    } catch (err) {
      console.error("Error fetching lessons", err);
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchLessons();
    const fetchQuiz = async () => {
      const packageId = localStorage.getItem('package_id');
      const token = localStorage.getItem('authToken');
      try {
        const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/mcqs/package/${packageId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.data?.mcqs?.length > 0) setQuizUrl(res.data.mcqs[0].url);
      } catch (e) { }
    };
    fetchQuiz();
  }, [fetchLessons]);

  const handleEnterLesson = (lessonId) => {
    localStorage.setItem('lesson_id', lessonId);
    navigate('/Video');
  };

  return (
    <div className={`dashboard-container ${darkMode ? 'dark-mode' : ''}`}>
      <div className="dashboard-content">

        {/* Header Section */}
        <header className="dashboard-header">
          <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}>
            <h1>مرحباً بك في <span className="highlight">محاضراتك</span></h1>
            <p>تابع تقدمك واستكمل مسيرتك التعليمية اليوم بكل شغف</p>
          </motion.div>

          {quizUrl && (
            <motion.a
              href={quizUrl} target="_blank" rel="noreferrer"
              className="quiz-banner-btn"
              whileHover={{ scale: 1.05, boxShadow: "0 10px 20px rgba(37, 99, 235, 0.2)" }}
              whileTap={{ scale: 0.95 }}
            >
              <FileText size={20} />
              <span>خوض الاختبار الشهري</span>
            </motion.a>
          )}
        </header>

        <div className="stats-row">
          <div className="stat-card">
            <div className="stat-icon-wrapper">
               <Layout className="icon" />
            </div>
            <div>
            <h1>إجمالي المحاضرات</h1>
            <h4>{lessons.length}</h4>
            </div>
          </div>
        </div>

        {/* Lessons List */}
        <div className="lessons-wrapper">
          {loading ? (
            <div className="skeleton-grid">
               {[1,2,3].map(i => <div key={i} className="skeleton-card"></div>)}
            </div>
          ) : lessons.length === 0 ? (
            <div className="empty-state">
              <AlertCircle size={48} />
              <p>لا توجد محاضرات متاحة حالياً</p>
            </div>
          ) : (
            <div className="lessons-grid">
              <AnimatePresence>
                {lessons.map((lesson, index) => {
                  const progress = lessonProgress[lesson.id] || { percentage: 0, status: 'not_started' };
                  const canAccess = lessonAccess[lesson.id] === true;
                  const isHovered = hoveredLockedId === lesson.id;

                  return (
                    <motion.div
                      key={lesson.id}
                      className={`lesson-modern-card ${!canAccess ? 'is-locked' : ''}`}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                    >
                      <div className="card-top">
                        <div className="image-container">
                          <img
                            src={`https://leader-acadmy.hwnix.com/storage/${lesson.image}`}
                            alt={lesson.title} 
                            className={!canAccess ? 'img-locked' : ''}
                          />
                          {!canAccess && (
                            <div className="lock-visual-overlay">
                               <Lock size={32} />
                            </div>
                          )}
                          <div className={`status-badge ${progress.status}`}>
                            {progress.status === 'completed' ? 'مكتمل' : progress.status === 'in_progress' ? 'مستمر' : 'جديد'}
                          </div>
                        </div>

                        <div className="lesson-info">
                          <h3>{lesson.title}</h3>
                          <p>{lesson.description || "ابدأ رحلة التعلم في هذا الدرس الآن."}</p>
                        </div>
                      </div>

                      <div className="card-bottom">
                        <AnimatePresence mode="wait">
                          {!canAccess && isHovered ? (
                            <motion.div 
                              className="incentive-box"
                              initial={{ opacity: 0, y: 10 }}
                              animate={{ opacity: 1, y: 0 }}
                              exit={{ opacity: 0, y: -10 }}
                            >
                              <Sparkles size={16} className="sparkle-icon" />
                              <span>يا بطل! خلص المحاضرة السابقة والامتحان لتفتح الفيديو 🚀</span>
                            </motion.div>
                          ) : (
                            <div className="progress-section">
                              <div className="progress-text">
                                <span>التقدم</span>
                                <span>{progress.percentage}%</span>
                              </div>
                              <div className="progress-bar-container">
                                <motion.div
                                  className="progress-bar-fill"
                                  initial={{ width: 0 }}
                                  animate={{ width: `${progress.percentage}%` }}
                                  transition={{ duration: 0.8 }}
                                />
                              </div>
                            </div>
                          )}
                        </AnimatePresence>

                        <div className="action-area">
                          {canAccess ? (
                            <button
                              className="enter-lesson-btn"
                              onClick={() => handleEnterLesson(lesson.id)}
                            >
                              <span>مشاهدة الآن</span>
                              <Play size={18} fill="currentColor" />
                            </button>
                          ) : (
                            <button
                              className="locked-action-btn"
                              onMouseEnter={() => setHoveredLockedId(lesson.id)}
                              onMouseLeave={() => setHoveredLockedId(null)}
                              onClick={() => setHoveredLockedId(lesson.id)}
                            >
                              <span>محتوى مغلق</span>
                              <Lock size={18} />
                            </button>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default ProfileCard;